import * as React from 'react';
import { useState, useEffect } from 'react';
import { useTranslate, useDataProvider } from 'react-admin';
import { useQuery } from '@tanstack/react-query';
import {
    Card,
    CardHeader,
    CardContent,
    IconButton,
    Tooltip,
    Typography,
    CircularProgress,
    Alert,
    Grid,
    ToggleButtonGroup,
    ToggleButton,
    Select,
    MenuItem,
    Stack,
    Box,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { LineChart } from '@mui/x-charts/LineChart';

const periodOptions = [
    { label: '1h', hours: 1, period: 60 },
    { label: '3h', hours: 3, period: 300 },
    { label: '6h', hours: 6, period: 300 },
    { label: '12h', hours: 12, period: 600 },
    { label: '24h', hours: 24, period: 900 },
    { label: '3d', hours: 72, period: 3600 },
    { label: '7d', hours: 168, period: 3600 },
];

const formatBytes = (bytes: number) => {
    if (bytes >= 1073741824) return `${(bytes / 1073741824).toFixed(1)} GB`;
    if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1)} MB`;
    if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${bytes.toFixed(0)} B`;
};

const formatBytesPerSec = (bytes: number) => `${formatBytes(bytes)}/s`;

const formatLatency = (seconds: number) => {
    if (seconds >= 1) return `${seconds.toFixed(2)} s`;
    return `${(seconds * 1000).toFixed(1)} ms`;
};

const metricCharts = [
    {
        id: 'cpu',
        titleKey: 'hallinta.rds_metrics.charts.cpu',
        color: '#1976d2',
        valueFormatter: (v: number | null) =>
            v != null ? `${v.toFixed(1)}%` : '',
    },
    {
        id: 'connections',
        titleKey: 'hallinta.rds_metrics.charts.connections',
        color: '#388e3c',
        valueFormatter: (v: number | null) =>
            v != null ? `${Math.round(v)}` : '',
    },
    {
        id: 'readLatency',
        titleKey: 'hallinta.rds_metrics.charts.readLatency',
        color: '#f57c00',
        valueFormatter: (v: number | null) =>
            v != null ? formatLatency(v) : '',
    },
    {
        id: 'writeLatency',
        titleKey: 'hallinta.rds_metrics.charts.writeLatency',
        color: '#d32f2f',
        valueFormatter: (v: number | null) =>
            v != null ? formatLatency(v) : '',
    },
    {
        id: 'readIOPS',
        titleKey: 'hallinta.rds_metrics.charts.readIOPS',
        color: '#7b1fa2',
        valueFormatter: (v: number | null) =>
            v != null ? `${v.toFixed(1)}` : '',
    },
    {
        id: 'writeIOPS',
        titleKey: 'hallinta.rds_metrics.charts.writeIOPS',
        color: '#c2185b',
        valueFormatter: (v: number | null) =>
            v != null ? `${v.toFixed(1)}` : '',
    },
    {
        id: 'freeableMemory',
        titleKey: 'hallinta.rds_metrics.charts.freeableMemory',
        color: '#00838f',
        valueFormatter: (v: number | null) => (v != null ? formatBytes(v) : ''),
    },
    {
        id: 'networkReceive',
        titleKey: 'hallinta.rds_metrics.charts.networkReceive',
        color: '#4527a0',
        valueFormatter: (v: number | null) =>
            v != null ? formatBytesPerSec(v) : '',
    },
    {
        id: 'networkTransmit',
        titleKey: 'hallinta.rds_metrics.charts.networkTransmit',
        color: '#283593',
        valueFormatter: (v: number | null) =>
            v != null ? formatBytesPerSec(v) : '',
    },
];

interface RdsMetricsPanelProps {
    clusters: Array<{ id: string; clusterIdentifier: string }>;
}

const RdsMetricsPanel = ({ clusters }: RdsMetricsPanelProps) => {
    const translate = useTranslate();
    const dataProvider = useDataProvider();
    const [selectedPeriod, setSelectedPeriod] = useState(1);
    const [cluster, setCluster] = useState('');

    useEffect(() => {
        if (!cluster && clusters.length > 0) {
            setCluster(clusters[0].clusterIdentifier);
        }
    }, [clusters, cluster]);

    const periodOpt = periodOptions[selectedPeriod];

    const { data, isLoading, error, refetch } = useQuery({
        queryKey: [
            'admin/rds-metrics',
            cluster,
            periodOpt.hours,
            periodOpt.period,
        ],
        queryFn: () =>
            dataProvider.getAny(
                'admin/rds-metrics',
                `cluster=${encodeURIComponent(cluster)}&hours=${periodOpt.hours}&period=${periodOpt.period}`
            ),
        refetchInterval: 60000,
        staleTime: 30000,
        enabled: !!cluster,
    });

    const metrics = data?.data?.metrics || {};

    return (
        <Card>
            <CardHeader
                title={translate('hallinta.rds_metrics.title')}
                action={
                    <Stack direction="row" spacing={1} alignItems="center">
                        {clusters.length > 1 && (
                            <Select
                                size="small"
                                value={cluster}
                                onChange={e => setCluster(e.target.value)}
                            >
                                {clusters.map(c => (
                                    <MenuItem
                                        key={c.id}
                                        value={c.clusterIdentifier}
                                    >
                                        {c.clusterIdentifier}
                                    </MenuItem>
                                ))}
                            </Select>
                        )}
                        <ToggleButtonGroup
                            size="small"
                            value={selectedPeriod}
                            exclusive
                            onChange={(_, v) => {
                                if (v !== null) setSelectedPeriod(v);
                            }}
                        >
                            {periodOptions.map((opt, idx) => (
                                <ToggleButton key={idx} value={idx}>
                                    {opt.label}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                        <Tooltip title={translate('ra.action.refresh')}>
                            <IconButton onClick={() => refetch()} size="small">
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                }
            />
            <CardContent>
                {!cluster && (
                    <Typography variant="body2" color="textSecondary">
                        {translate('hallinta.rds_metrics.no_cluster')}
                    </Typography>
                )}
                {isLoading && (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            py: 4,
                        }}
                    >
                        <CircularProgress />
                    </Box>
                )}
                {error && (
                    <Alert severity="error">
                        {translate('hallinta.error')}:{' '}
                        {(error as Error).message}
                    </Alert>
                )}
                {!isLoading && !error && cluster && (
                    <Grid container spacing={2}>
                        {metricCharts.map(chart => {
                            const metric = metrics[chart.id];
                            if (
                                !metric ||
                                !metric.timestamps ||
                                metric.timestamps.length === 0
                            ) {
                                return null;
                            }
                            const xData = metric.timestamps.map(
                                (t: string) => new Date(t)
                            );
                            return (
                                <Grid
                                    key={chart.id}
                                    size={{ xs: 12, md: 6, lg: 4 }}
                                >
                                    <Typography
                                        variant="subtitle2"
                                        gutterBottom
                                        sx={{ pl: 1 }}
                                    >
                                        {translate(chart.titleKey)}
                                    </Typography>
                                    <LineChart
                                        height={200}
                                        xAxis={[
                                            {
                                                data: xData,
                                                scaleType: 'time',
                                                valueFormatter: (v: Date) =>
                                                    v.toLocaleTimeString(
                                                        'fi-FI',
                                                        {
                                                            hour: '2-digit',
                                                            minute: '2-digit',
                                                        }
                                                    ),
                                            },
                                        ]}
                                        series={[
                                            {
                                                data: metric.values,
                                                color: chart.color,
                                                showMark: false,
                                                valueFormatter:
                                                    chart.valueFormatter,
                                            },
                                        ]}
                                        margin={{
                                            left: 50,
                                            right: 10,
                                            top: 10,
                                            bottom: 25,
                                        }}
                                    />
                                </Grid>
                            );
                        })}
                    </Grid>
                )}
            </CardContent>
        </Card>
    );
};

export default RdsMetricsPanel;
